import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Edit2, Save, X, Brain, MessageSquare } from "lucide-react";
import { toast } from "sonner";

interface QAPair {
  id: string;
  question: string;
  answer: string;
  category: string;
  is_active: boolean;
  usage_count: number;
  created_at: string;
}

const CATEGORIES = [
  "general",
  "tools",
  "authentication",
  "billing",
  "technical",
  "troubleshooting"
];

export function BotTraining() {
  const [qaPairs, setQaPairs] = useState<QAPair[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [filterCategory, setFilterCategory] = useState<string>("all");
  
  const [newQuestion, setNewQuestion] = useState("");
  const [newAnswer, setNewAnswer] = useState("");
  const [newCategory, setNewCategory] = useState("general");
  
  const [editQuestion, setEditQuestion] = useState("");
  const [editAnswer, setEditAnswer] = useState("");
  const [editCategory, setEditCategory] = useState("");

  useEffect(() => {
    fetchQAPairs();
  }, []);

  const fetchQAPairs = async () => {
    const { data, error } = await supabase
      .from("bot_training")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      toast.error("Failed to load Q&A pairs");
      console.error(error);
    } else {
      setQaPairs(data || []);
    }
    setLoading(false);
  };

  const addQAPair = async () => {
    if (!newQuestion.trim() || !newAnswer.trim()) {
      toast.error("Question and answer are required");
      return;
    }

    const { error } = await supabase.from("bot_training").insert({
      question: newQuestion.trim(),
      answer: newAnswer.trim(),
      category: newCategory,
    });

    if (error) {
      toast.error("Failed to add Q&A pair");
      console.error(error);
    } else {
      toast.success("Q&A pair added successfully");
      setNewQuestion("");
      setNewAnswer("");
      setNewCategory("general");
      setShowAddForm(false);
      fetchQAPairs();
    }
  };

  const updateQAPair = async (id: string) => {
    const { error } = await supabase
      .from("bot_training")
      .update({
        question: editQuestion.trim(),
        answer: editAnswer.trim(),
        category: editCategory,
      })
      .eq("id", id);

    if (error) {
      toast.error("Failed to update Q&A pair");
    } else {
      toast.success("Q&A pair updated");
      setEditingId(null);
      fetchQAPairs();
    }
  };

  const toggleActive = async (id: string, currentState: boolean) => {
    const { error } = await supabase
      .from("bot_training")
      .update({ is_active: !currentState })
      .eq("id", id);

    if (error) {
      toast.error("Failed to toggle status");
    } else {
      fetchQAPairs();
    }
  };

  const deleteQAPair = async (id: string) => {
    if (!confirm("Delete this Q&A pair?")) return;

    const { error } = await supabase.from("bot_training").delete().eq("id", id);

    if (error) {
      toast.error("Failed to delete Q&A pair");
    } else {
      toast.success("Q&A pair deleted");
      fetchQAPairs();
    }
  };

  const startEdit = (pair: QAPair) => {
    setEditingId(pair.id);
    setEditQuestion(pair.question);
    setEditAnswer(pair.answer);
    setEditCategory(pair.category);
  };

  const filteredPairs = filterCategory === "all" 
    ? qaPairs 
    : qaPairs.filter(p => p.category === filterCategory);

  const activeCount = qaPairs.filter(p => p.is_active).length;

  if (loading) {
    return <div className="p-4 text-muted-foreground">Loading training data...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-primary" />
              <div>
                <p className="text-2xl font-bold">{qaPairs.length}</p>
                <p className="text-xs text-muted-foreground">Total Q&A Pairs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{activeCount}</p>
                <p className="text-xs text-muted-foreground">Active Pairs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">
                  {qaPairs.reduce((sum, p) => sum + p.usage_count, 0)}
                </p>
                <p className="text-xs text-muted-foreground">Times Used</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label>Filter by category:</Label>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => setShowAddForm(true)} disabled={showAddForm}>
          <Plus className="h-4 w-4 mr-2" />
          Add Q&A Pair
        </Button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <Card className="border-primary">
          <CardHeader>
            <CardTitle className="text-lg">Add New Q&A Pair</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Question (what users might ask)</Label>
              <Input
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                placeholder="e.g., How do I check my email deliverability?"
              />
            </div>
            <div>
              <Label>Answer (bot's response)</Label>
              <Textarea
                value={newAnswer}
                onChange={(e) => setNewAnswer(e.target.value)}
                placeholder="Provide a helpful, detailed answer..."
                rows={4}
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={newCategory} onValueChange={setNewCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button onClick={addQAPair}>
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
              <Button variant="outline" onClick={() => setShowAddForm(false)}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Q&A List */}
      <div className="space-y-3">
        {filteredPairs.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              No Q&A pairs found. Add some to train the bot!
            </CardContent>
          </Card>
        ) : (
          filteredPairs.map((pair) => (
            <Card key={pair.id} className={!pair.is_active ? "opacity-60" : ""}>
              <CardContent className="py-4">
                {editingId === pair.id ? (
                  <div className="space-y-3">
                    <Input
                      value={editQuestion}
                      onChange={(e) => setEditQuestion(e.target.value)}
                    />
                    <Textarea
                      value={editAnswer}
                      onChange={(e) => setEditAnswer(e.target.value)}
                      rows={3}
                    />
                    <Select value={editCategory} onValueChange={setEditCategory}>
                      <SelectTrigger className="w-[150px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => updateQAPair(pair.id)}>
                        <Save className="h-3 w-3 mr-1" />
                        Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline">{pair.category}</Badge>
                          <span className="text-xs text-muted-foreground">
                            Used {pair.usage_count} times
                          </span>
                        </div>
                        <p className="font-medium text-sm">Q: {pair.question}</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          A: {pair.answer.length > 150 ? pair.answer.slice(0, 150) + "..." : pair.answer}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <div className="flex items-center gap-1">
                          <Switch
                            checked={pair.is_active}
                            onCheckedChange={() => toggleActive(pair.id, pair.is_active)}
                          />
                          <span className="text-xs">{pair.is_active ? "Active" : "Inactive"}</span>
                        </div>
                        <Button size="icon" variant="ghost" onClick={() => startEdit(pair)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => deleteQAPair(pair.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
